import { Saga } from 'redux-saga'
import {
  ActionPattern,
  all,
  call,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects'
import { api } from 'src/configuration'
import menuMock from 'src/hardcoded-data/menu.json'
import footerMenuMock from 'src/hardcoded-data/footer.json'
import Bugsnag from '@bugsnag/js'
import { Season } from 'src/types/fam-graphql'

import { actions, selectors } from '..'
import I18nSagas from '../i18n/sagas'
import SettingsSagas from '../settings/sagas'
import OnboardingSagas from '../onboarding/sagas'
import UserSagas from '../user/sagas'
import Router from '../../routes/router'
import routes from '../../routes/definitions'
import {
  CacheNames,
  CacheType,
  getJsonCacheEntry,
} from '../../utils/CacheUtils'

export default class AppSagas {
  static *init(ctx: any, pageSaga: any) {
    const isInit = yield select(selectors.app.isInit)

    if (!process.browser) {
      yield all([
        call(I18nSagas.initWithLocale, 'fr'),
        call(SettingsSagas.loadSettings),
        call(OnboardingSagas.loadTips),
        ...(api.MOCK_MENUS
          ? [call(AppSagas.mockMenus)]
          : [call(AppSagas.getMenu), call(AppSagas.getFooterMenu)]),
      ])
    }

    yield call(AppSagas.setSeason)
    const initialProps = pageSaga ? yield call(pageSaga, ctx) : {}

    if (ctx.res && ctx.res.statusCode > 400) {
      console.log(
        'AppSagas - error',
        ctx.res.statusCode,
        ctx.res.statusMessage,
        {
          asPath: ctx.asPath,
          query: ctx.query,
          pathname: ctx.pathname,
          err: ctx.err,
        }
      )
      ctx.res.statusCode = 404
    }

    if (ctx.res && initialProps && initialProps.error) {
      ctx.res.statusCode = initialProps.error
    }

    /*if (ctx.res && ctx.res.statusCode === 404 && app.APP_ENV !== 'local') {
      const url = 'https://www.mangerbouger.fr/404'
      ctx.res.redirect(301, url)
    }*/

    if (!isInit) {
      yield put(actions.app.setIsInit({ isInit: true }))
    }

    return initialProps
  }

  static *onMounted() {
    if (window && window.localStorage) {
      const afterLoginJobs = JSON.parse(
        window.localStorage.getItem('afterLoginJobs') || '[]'
      )
      yield put(actions.app.setAfterLoginJobs(afterLoginJobs))
    }
    yield call(UserSagas.init)
  }

  static *onMountedOffline() {
    console.log('[MOUNT OFFLINE] offline and has logged in')
    yield put(actions.app.setIsInit({ isInit: true }))
    yield put(actions.app.setIsLoading(false))
    yield put(actions.user.setIsInit(true))
    const labels = yield call(
      getJsonCacheEntry,
      CacheNames[CacheType.data],
      'translations'
    )
    console.log('labels', labels)
    if (labels) {
      yield call(I18nSagas.initWithLocaleAndLabels, 'fr', labels)
    }

    const currentPlan = yield call(
      getJsonCacheEntry,
      CacheNames[CacheType.data],
      'currentPlan'
    )
    console.log('currentPlan', currentPlan)
    if (currentPlan) {
      yield put(actions.user.setCurrentPlan(currentPlan))
    }

    const notificationPreferences = yield call(
      getJsonCacheEntry,
      CacheNames[CacheType.data],
      'notificationPreferences'
    )
    console.log('notificationPreferences', notificationPreferences)
    if (notificationPreferences) {
      yield put(
        actions.user.setNotificationPreferences({
          shouldReceiveNotifications:
            notificationPreferences.should_receive_notifications,
          notificationFrequency: notificationPreferences.notification_frequency,
        })
      )
    }

    Router.pushRoute(routes.planning.name)
  }

  static *mockMenus() {
    if (api.MOCK_MENUS) {
      yield all([
        put(actions.app.setMenu({ menu: menuMock })),
        put(
          actions.app.setFooterData({
            menu: footerMenuMock.menu,
            legals: footerMenuMock.footerLegal,
          })
        ),
      ])
      return
    }
  }

  static *getMenu() {
    const result = yield call(() =>
      fetch(api.MENU_ENDPOINT as RequestInfo)
        .then((res) => res.json())
        .catch((e) => ({ errors: e }))
    )
    if (result.errors) {
      Bugsnag.notify(result.errors)
      return
    }
    yield put(actions.app.setMenu({ menu: result }))
  }

  static *getFooterMenu() {
    if (api.MOCK_MENUS) {
      return
    }
    const result = yield call(() =>
      fetch(api.FOOTER_ENDPOINT as RequestInfo)
        .then((res) => res.json())
        .catch((e) => ({ errors: e }))
    )
    if (result.errors) {
      Bugsnag.notify(result.errors)
      return
    }
    yield put(
      actions.app.setFooterData({
        menu: result.menu || null,
        legals: result.footerLegal,
      })
    )
  }

  static *setSeason() {
    const stateSeason = yield select(selectors.app.currentSeason)

    if (stateSeason) {
      return
    }

    const currentMonth = new Date().getMonth() + 1

    const currentSeason = [12, 1, 2].includes(currentMonth)
      ? Season.Winter
      : [3, 4, 5].includes(currentMonth)
      ? Season.Spring
      : [6, 7, 8].includes(currentMonth)
      ? Season.Summer
      : Season.Autumn

    yield put(actions.app.setCurrentSeason({ currentSeason }))
  }

  static *loop() {
    yield all([
      takeLatest(actions.app.init as ActionPattern, this.init as Saga),
      takeLatest(actions.app.mount as ActionPattern, this.onMounted as Saga),
      takeLatest(
        actions.app.mountOffline as ActionPattern,
        this.onMountedOffline as Saga
      ),
    ])
  }
}
